import { Dialog } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@shared/authentication/auth.service';
import {
  KFM_APP_DBPATH,
  KFM_APP_INITIAL_DOWNLOAD_TIME,
  KFM_APP_INITIAL_UPLOAD_TIME,
  KFM_APP_LOGIN_ID,
  KFM_APP_SETTINGS,
  KFM_CLOUD_WORKER_SETTINGS,
  KFM_BOOKING_MIGRATION_STATUS,
  KFM_WEB_LAST_CLOUD_SYNC,
} from '@shared/constants/constants';
import { LocalStorageService } from '@shared/storage/local-storage.service';
import { KfmInfoDialogComponent } from '@shared/ui/components/features/dialogs/info-dialog/kfm-info-dialog.component';
import { KfmSpinnerDialogComponent } from '@shared/ui/components/features/dialogs/spinner-dialog/kfm-spinner-dialog.component';
import { CloudWorkerSyncService } from './cloud-worker-sync.service';
import { HsBlazorService } from '@hsetu/core-services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class KaufmannSyncService {
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _authService = inject(AuthService);
  private readonly _localStorage = inject(LocalStorageService);
  private readonly _dialog = inject(Dialog);
  private readonly _cloudWorkerSyncService = inject(CloudWorkerSyncService);

  private readonly _serviceKey = 'kfmDatabase';

  public async clearLocalData(): Promise<void> {
    const hasChanges = await this.hasLocalChanges();

    if (hasChanges) {
      // Show warning that local changes will be lost
      this._dialog.open(KfmInfoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: 'Fehler',
          text: 'Lokale Änderungen müssen zuerst synchronisiert werden, bevor die lokalen Daten gelöscht werden können.',
        },
      });

      return;
    }

    try {
      this._dialog.open(KfmSpinnerDialogComponent, {
        data: { text: 'Lösche lokale Daten...' },
      });
      const deletedResult = await this._blazorService.invokeService(
        this._serviceKey,
        'Delete',
      );

      let deleted = false;

      if (deletedResult) {
        deleted = (deletedResult as Record<string, boolean>)['deleted'];
      }

      if (deleted) {
        await this._localStorage.remove(KFM_APP_DBPATH);
        await this._localStorage.remove(KFM_APP_INITIAL_DOWNLOAD_TIME);
        await this._localStorage.remove(KFM_APP_INITIAL_UPLOAD_TIME);
        await this._localStorage.remove(KFM_APP_LOGIN_ID);
        await this._localStorage.remove(KFM_APP_SETTINGS);
        await this._localStorage.remove(KFM_WEB_LAST_CLOUD_SYNC);
        await this._localStorage.remove(KFM_CLOUD_WORKER_SETTINGS);
        await this._localStorage.remove(KFM_BOOKING_MIGRATION_STATUS);

        this._cloudWorkerSyncService.stopDomainSyncs();
        this._cloudWorkerSyncService.clearAllSyncData();

        this._authService.logout(environment.auth.configId).subscribe(() => {
          console.log('App component - logoffAndRevokeTokens');
        });

        // this._router.navigate([LOGIN_PAGE]);

        // window.location.reload();
      }
    } finally {
      this._dialog.closeAll();
    }
  }

  private async hasLocalChanges(): Promise<boolean> {
    const hasChangesResponse = await this._blazorService.invokeService(
      this._serviceKey,
      'HasLocalChanges',
    );

    return hasChangesResponse as boolean;
  }
}
